import { useEffect, useMemo, useState } from 'react'
import { formatISO, parse, subYears } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'
import { toast } from 'react-hot-toast'

import { ScrollableModal } from 'components/organisms/modals/scrollable-modal'
import { Alert, PasswordMaskField } from 'components/common'
import { Button } from 'components/atoms/button'
import { ErrorText } from 'components/atoms/ErrorText'
import { Input } from 'components/atoms/Input'
import { Label } from 'components/atoms/Label'
import { ResponsiveDatePicker } from 'components/atoms/responsive-date-picker'
import { PhoneField } from 'components/molecules/phone-field'
import { formatNationalNumber } from 'components/molecules/phone-field/format-national-number'
import { isSupportedCountry } from 'components/molecules/phone-field/supported-countries'
import { useAuth } from 'hooks/useAuth'
import { useStore } from 'hooks/useStore'
import { useCompleteProfileMutation } from 'graphql/generated/scheme'
import { trueErrorMessage } from 'utils/error'
import { passwordSchema } from 'utils/password'

const INPUT_CLASSNAME =
	'px-4 py-3 pr-16 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12 focus:border-accent-surface focus:ring-1'

const LABEL_CLASSNAME = 'block normal-case mb-2'

const emailSchema = Yup.string()
	.trim()
	.transform(value => value?.toLowerCase())
	.email('Your email address is invalid. Please try again.')
	.required('Email is required')

const DEFAULT_COUNTRY = 'us'

const asCountryCode = (country: string) => country.toUpperCase() as CountryCode

/**
 * Splits a stored number into the country and national number `PhoneField` edits. A number it cannot
 * resolve to one country (an admin's typo) stays under the default country, so validation can flag it.
 * Its digits go through the field's own formatter, so the first keystroke does not rewrite the text.
 */
const splitPhoneNumber = (stored?: string | null) => {
	const parsed = stored ? parsePhoneNumberFromString(stored) : undefined
	// A restricted country is not in the selector, so it is treated like a number that cannot be placed.
	if (parsed?.country && isSupportedCountry(parsed.country))
		return { country: parsed.country.toLowerCase(), national: parsed.formatNational() }

	// The selector already shows the dial code, so drop it only when it is the default country's own.
	const sharesDialCode = parsed?.countryCallingCode === getCountryCallingCode(asCountryCode(DEFAULT_COUNTRY))
	return {
		country: DEFAULT_COUNTRY,
		national: formatNationalNumber(sharesDialCode ? parsed.nationalNumber : (stored ?? ''), DEFAULT_COUNTRY),
	}
}

const makeValidationSchema = (country: string) =>
	Yup.object().shape({
		emailAddress: emailSchema,
		phoneNumber: Yup.string()
			.required('Your phone number is invalid. Please try again.')
			.test('isValidPhoneNumber', 'Your phone number is invalid. Please try again.', value =>
				isValidPhoneNumber(value ?? '', asCountryCode(country))
			),
		dateOfBirth: Yup.date()
			.required('Must enter date of birth')
			.typeError('Date of birth is required')
			.min(new Date(1930, 0, 1), 'Date of birth must be January 1, 1930 or later')
			.max(subYears(new Date(), 14), 'You must be at least 14 years old'),
		newPassword: passwordSchema,
		passwordConfirmation: Yup.string()
			.required('Confirm password is required')
			.oneOf(
				[Yup.ref('newPassword')],
				'Your confirm password does not match with new password. Please try again.'
			),
	})

/**
 * One-time, blocking form for accounts an admin created. It stays open until the server reports the
 * profile is complete, so it must never be unmounted by a route change below it.
 */
export function ProfileCompletionModal() {
	const { logout } = useAuth()
	const { user, refetchUser, clearStore } = useStore()
	const [message, setMessage] = useState<any>(null)
	// The account's email is what its password is keyed on; it is prefilled but the owner can correct it.
	const initialEmailAddress = user?.emailAddress || user?.userName || ''
	const [initialPhone] = useState(() => splitPhoneNumber(user?.phoneNumber))
	const [country, setCountry] = useState(initialPhone.country)
	const validationSchema = useMemo(() => makeValidationSchema(country), [country])
	const [completeProfile] = useCompleteProfileMutation({
		onError: error => {
			setMessage({ description: trueErrorMessage(error), type: 'danger' })
		},
	})
	const {
		control,
		register,
		handleSubmit,
		trigger,
		getValues,
		formState: { errors, isValid, isSubmitting },
	} = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			emailAddress: initialEmailAddress,
			phoneNumber: initialPhone.national || undefined,
			dateOfBirth: user?.dateOfBirth ? parse(user.dateOfBirth, 'yyyy-MM-dd', new Date()) : undefined,
		},
	})

	// An admin may have entered an invalid email or phone, and `mode: 'onChange'` stays silent until a field is
	// edited. Validate the prefilled fields once on mount so their errors are visible straight away.
	useEffect(() => {
		const prefilled = (['emailAddress', 'dateOfBirth'] as const).filter(name => getValues(name))
		if (prefilled.length) void trigger(prefilled)
	}, [trigger, getValues])

	// The phone's validity depends on its country, so re-check it on mount (prefill) and whenever the country changes.
	useEffect(() => {
		if (getValues('phoneNumber')) void trigger('phoneNumber')
	}, [country, trigger, getValues])

	// handleSubmit keeps `isSubmitting` true until this promise settles, so the form stays locked across the refetch.
	const onSubmit = async (values: any) => {
		setMessage(null)
		// The form holds the national number; the API takes E.164. Validation has already passed for this country.
		const phoneNumber = parsePhoneNumberFromString(values.phoneNumber, asCountryCode(country))?.number
		if (!phoneNumber) return
		// Failures surface through the mutation's onError, which leaves `data` undefined.
		const { data } = await completeProfile({
			variables: {
				args: {
					emailAddress: values.emailAddress,
					phoneNumber,
					dateOfBirth: formatISO(values.dateOfBirth, { representation: 'date' }),
					newPassword: values.newPassword,
				},
			},
		})
		if (!data) return

		// The modal closes when the refetched user clears `profileCompletionRequired`, in the same update that
		// recomputes `authInfo.isComplete`. Awaiting it keeps the guards from routing to sign-up-2 in between.
		// Apollo resolves a failed lazy query with `error` rather than rejecting; the store already toasts it.
		const refetched = await refetchUser().catch(() => undefined)
		// Judge completion by the refetched user, the same data that closes the modal, so the toast cannot disagree.
		const stillRequired = refetched?.data?.me?.profileCompletionRequired ?? true
		if (!refetched?.error && !stillRequired) toast.success('Your profile is complete.')
	}

	const handleUseAnotherAccount = () => {
		logout(null, null, () => {
			// Clear the stored session first: the login page must not read a token this tab is leaving behind.
			clearStore().finally(() => window.location.replace('/auth/login'))
		})
	}

	return (
		<ScrollableModal
			open
			// Blocking: the account cannot use the app until the profile is complete, so there is no close path.
			onClose={() => {}}
			ariaLabel="Finish setting up your account"
			surfaceClassName="bg-primary-surface/[0.7]"
			header={
				<h2 className="pt-5 text-2xl font-bold tracking-tight text-primary-text">
					Finish setting up your account
				</h2>
			}
			footer={
				<div className="space-y-4">
					{/* The submit button lives here, so a server error must be visible — and announced — from here too. */}
					{message && (
						<div role="alert">
							<Alert message={message} />
						</div>
					)}
					<Button
						variant="primary"
						fullWidth
						size="lg"
						type="submit"
						// The footer sits outside the form, so it submits the form by id.
						form="profile-completion-form"
						className="flex items-center justify-center gap-2"
						disabled={!isValid || isSubmitting}
					>
						{isSubmitting && <FontAwesomeIcon icon={['fas', 'spinner']} spin aria-hidden />}
						Save and continue
					</Button>
					<button
						type="button"
						className="block w-full rounded text-center text-sm text-secondary-text hover:text-primary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-surface"
						onClick={handleUseAnotherAccount}
					>
						Use another account
					</button>
				</div>
			}
		>
			<div className="text-left">
				<p className="text-sm leading-relaxed text-secondary-text">
					This account was created for you. Confirm your details and choose the password you will use from now
					on.
				</p>
				<form id="profile-completion-form" className="mt-6" onSubmit={handleSubmit(onSubmit)}>
					<div className="space-y-4">
						<div>
							<Label htmlFor="profile-completion-email" label="Email" className={LABEL_CLASSNAME} />
							<div className="relative">
								<span
									aria-hidden="true"
									className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-primary-text"
								>
									<FontAwesomeIcon icon={['fas', 'envelope']} />
								</span>
								<Input
									id="profile-completion-email"
									type="email"
									autoComplete="email"
									className="pl-11"
									aria-invalid={!!errors.emailAddress}
									aria-describedby={
										errors.emailAddress ? 'profile-completion-email-error' : undefined
									}
									{...register('emailAddress')}
								/>
							</div>
							{errors.emailAddress && (
								<ErrorText
									id="profile-completion-email-error"
									className="mt-2"
									message={errors.emailAddress.message as string}
								/>
							)}
						</div>
						<div>
							<Label
								htmlFor="profile-completion-phone"
								label="Phone number"
								className={LABEL_CLASSNAME}
							/>
							<Controller
								control={control}
								name="phoneNumber"
								render={({ field }) => (
									<PhoneField
										id="profile-completion-phone"
										country={country}
										onCountryChange={setCountry}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										hasError={!!errors.phoneNumber}
										ariaDescribedBy={
											errors.phoneNumber ? 'profile-completion-phone-error' : undefined
										}
									/>
								)}
							/>
							{errors.phoneNumber && (
								<ErrorText
									id="profile-completion-phone-error"
									className="mt-2"
									message={errors.phoneNumber.message as string}
								/>
							)}
						</div>
						<div>
							{/* The picker is three inputs, so a label cannot point at one of them; it names each one instead. */}
							<Label label="Date of birth" className={LABEL_CLASSNAME} />
							<Controller
								control={control}
								name="dateOfBirth"
								render={({ field }) => (
									<ResponsiveDatePicker
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										ariaLabel="Date of birth"
										align="start"
										defaultActiveStartDate={subYears(new Date(), 14)}
									/>
								)}
							/>
							{errors.dateOfBirth && (
								<ErrorText className="mt-2" message={errors.dateOfBirth.message as string} />
							)}
						</div>
						<div className="flex items-center gap-3 pt-2">
							<span className="h-px flex-1 bg-translucent-plus-border" aria-hidden="true" />
							<span className="text-xs font-semibold uppercase tracking-[0.08em] text-secondary-text">
								Create your password
							</span>
							<span className="h-px flex-1 bg-translucent-plus-border" aria-hidden="true" />
						</div>
						<div>
							<Label
								htmlFor="profile-completion-new-password"
								label="New password"
								className={LABEL_CLASSNAME}
							/>
							<Controller
								control={control}
								name="newPassword"
								render={({ field }) => (
									<PasswordMaskField
										id="profile-completion-new-password"
										autoComplete="new-password"
										className={INPUT_CLASSNAME}
										placeholder="New password"
										toggleLabel="new password"
										aria-invalid={!!errors.newPassword}
										aria-describedby={
											errors.newPassword ? 'profile-completion-new-password-error' : undefined
										}
										value={field.value ?? ''}
										onChange={field.onChange}
										onBlur={field.onBlur}
									/>
								)}
							/>
							{errors.newPassword && (
								<ErrorText
									id="profile-completion-new-password-error"
									className="mt-2"
									message={errors.newPassword.message as string}
								/>
							)}
						</div>
						<div>
							<Label
								htmlFor="profile-completion-confirm-password"
								label="Confirm new password"
								className={LABEL_CLASSNAME}
							/>
							<Controller
								control={control}
								name="passwordConfirmation"
								render={({ field }) => (
									<PasswordMaskField
										id="profile-completion-confirm-password"
										autoComplete="new-password"
										className={INPUT_CLASSNAME}
										placeholder="Confirm new password"
										toggleLabel="confirm password"
										aria-invalid={!!errors.passwordConfirmation}
										aria-describedby={
											errors.passwordConfirmation
												? 'profile-completion-confirm-password-error'
												: undefined
										}
										value={field.value ?? ''}
										onChange={field.onChange}
										onBlur={field.onBlur}
									/>
								)}
							/>
							{errors.passwordConfirmation && (
								<ErrorText
									id="profile-completion-confirm-password-error"
									className="mt-2"
									message={errors.passwordConfirmation.message as string}
								/>
							)}
						</div>
					</div>
				</form>
			</div>
		</ScrollableModal>
	)
}
